import { AGENCY_NUMBER, findBookingType } from "../../../_constants/constant";
import {
  formatBookingData,
  formatTempBookingData,
  formatTraveller,
} from "../../../_helpers/bookingUtils";
import { getSelectedItems } from "../../../_helpers/miscUtils";
import { formatSSRData } from "../../../_helpers/ssrUtils";
import { getLocalItem } from "../../../_helpers/storageUtils";
import { ErrorToast } from "../../../toast/Toasts";
import { getBookingDetails } from "../../mytrips/services/mytrips.services";
import {
  addPayment,
  confirmFlightBooking,
  createPaymentOrder,
  flightTempBooking,
  saveFlightBooking,
  savePanInfo,
  saveTempFlightBooking,
  verifyPayment,
} from "../services/reviewbooking.services";

export const useFormSubmit = ({
  formik,
  flightReviewDetails,
  location,
  navigate,
  dispatch,
  totalAmount,
  totalSeatsAmount,
  totalBaggageAmount,
  totalWheelChairAmount,
  totalMealAmount,
  MAX_PAN_REQUIRED_AMOUNT,
  billingData,
  totalSeats,
  selectedmealPerSegment,
  selectedBaggagePerSegment,
  selectedWheelChairPerSegment,
  convenienceFeeWithGST,
  handlingFees,
  appliedPromo,
  appliedPromoAmount,
  appliedGiftCode,
  appliedGiftCodeAmount,
  walletAmount,
  walletRefundableAmount,
  walletNonRefundableAmount,
  fieldRefs,
  ADULT,
  CHILD,
  INFANT,
  AdultRequireDetails,
  ChildRequireDetails,
  InfantRequireDetails,
  tempBookingRef,
  setTempBookingRef,
  portalBookingNo,
  setPortalBookingNo,
  setIsProcessingPayment,
  isProcessingPayment,
  creditLimitAmount,
}: {
  formik: any;
  flightReviewDetails: any;
  location: any;
  navigate: any;
  dispatch: any;
  totalAmount: number;
  totalSeatsAmount: number;
  totalBaggageAmount: number;
  totalWheelChairAmount: number;
  totalMealAmount: number;
  MAX_PAN_REQUIRED_AMOUNT: number;
  billingData: any;
  totalSeats: any[];
  selectedmealPerSegment: any;
  selectedBaggagePerSegment: any;
  selectedWheelChairPerSegment: any;
  convenienceFeeWithGST: number;
  handlingFees: any;
  appliedPromo: any;
  appliedPromoAmount: number;
  appliedGiftCode: any;
  appliedGiftCodeAmount: number;
  walletAmount: number;
  walletRefundableAmount: number;
  walletNonRefundableAmount: number;
  fieldRefs: any;
  ADULT: number;
  CHILD: number;
  INFANT: number;
  AdultRequireDetails: any;
  ChildRequireDetails: any;
  InfantRequireDetails: any;
  tempBookingRef: string | null;
  setTempBookingRef: React.Dispatch<React.SetStateAction<string | null>>;
  portalBookingNo: string | null;
  setPortalBookingNo: React.Dispatch<React.SetStateAction<string | null>>;
  setIsProcessingPayment: React.Dispatch<React.SetStateAction<boolean>>;
  isProcessingPayment: boolean;
  creditLimitAmount: number;
}) => {
  const handleSubmit = async (values: any) => {
    let errorMessages: string[] = [];
    let firstInvalidField: string | null = null;

    const requiredItems = [
      {
        type: "adults",
        key: "ADULT",
        label: "Adult",
        count: ADULT,
      },
      {
        type: "children",
        key: "CHILD",
        label: "Child",
        count: CHILD,
      },
      {
        type: "infants",
        key: "INFANT",
        label: "Infant",
        count: INFANT,
      },
    ];

    requiredItems.forEach((item) => {
      const selectedItems = getSelectedItems(
        values[item.type as keyof typeof values]
      )?.length;
      if (selectedItems < item.count) {
        errorMessages.push(`${item.count} ${item.label}`);

        if (!firstInvalidField) {
          firstInvalidField = item.type;
        }
      }
    });

    if (errorMessages.length > 0) {
      if (firstInvalidField && fieldRefs.current[firstInvalidField]) {
        fieldRefs.current[firstInvalidField].focus();
      }
      return ErrorToast(`You must have to select ${errorMessages.join(", ")}.`);
    }

    if (
      totalAmount >= MAX_PAN_REQUIRED_AMOUNT &&
      !billingData?.billing_pan_card_no
    ) {
      if (!values?.billing_pan_card_name || !values?.billing_pan_card_name) {
        return ErrorToast(
          "Please enter a valid PAN number and verify it to proceed"
        );
      } else {
        dispatch(
          savePanInfo({
            user_guid: getLocalItem("user_guid"),
            billing_pan_card_no: values?.billing_pan_card_no,
            billing_pan_card_name: values?.billing_pan_card_name,
          })
        );
      }
    }

    const travellerInfo = [
      ...values.adults
        .filter((e: any) => e.isSelected && ADULT > 0)
        .map((adult: any) => formatTraveller(adult, "0", AdultRequireDetails)),
      ...values.children
        .filter((e: any) => e.isSelected && CHILD > 0)
        .map((child: any) => formatTraveller(child, "1", ChildRequireDetails)),
      ...values.infants
        .filter((e: any) => e.isSelected && INFANT > 0)
        .map((infant: any) =>
          formatTraveller(infant, "2", InfantRequireDetails)
        ),
    ];

    const updatedTravellerInfo = travellerInfo.map(
      (traveller: any, index: number) => ({
        ...traveller,
        Pax_Id: index + 1,
      })
    );

    const Search_Key = location.state?.Search_Key || "";

    const BookingFlightDetails = flightReviewDetails
      ? flightReviewDetails?.map((flight: any) => {
          return {
            Search_Key: Search_Key,
            Flight_Key: flight?.Flight?.Flight_Key,
            BookingSSRDetails: formatSSRData(
              totalSeats,
              selectedmealPerSegment,
              selectedBaggagePerSegment,
              selectedWheelChairPerSegment,
              flight
            ),
          };
        })
      : [];

    // Prevent multiple simultaneous submissions
    if (isProcessingPayment) {
      ErrorToast("Payment is already being processed. Please wait.");
      return;
    }

    setIsProcessingPayment(true);

    try {
      let bookingRefNo = tempBookingRef || "";
      let currentPortalBookingNo = portalBookingNo || "";
      let tempbookingRes: any; // Declare here
      let tempRes: any; // Declare here

      if (!bookingRefNo) {
        const initialTempPayload = {
          PAX_Details: updatedTravellerInfo,
          Customer_Mobile: AGENCY_NUMBER,
          Passenger_Mobile: `${values?.mobile_no}`,
          WhatsAPP_Mobile: `${values?.mobile_no}`,
          Passenger_Email: values?.email_id,
          GST: values?.isGST,
          GST_Number: values?.isGST ? values?.gst_number : "",
          GST_HolderName: values?.isGST ? values?.gst_registered_name : "",
          GST_Address: values?.isGST ? values?.gst_address : "",
          BookingFlightDetails: BookingFlightDetails,
          CostCenterId: 0,
          ProjectId: 0,
          BookingRemark: "",
          CorporateStatus: 0,
          CorporatePaymentMode: 0,
          MissedSavingReason: null,
          CorpTripType: null,
          CorpTripSubType: null,
          TripRequestId: null,
          BookingAlertIds: null,
        };

        tempbookingRes = await dispatch(flightTempBooking(initialTempPayload));

        if (!tempbookingRes?.payload?.Booking_RefNo) {
          throw new Error("Temp booking failed");
        }

        bookingRefNo = tempbookingRes?.payload?.Booking_RefNo;
        setTempBookingRef(bookingRefNo);
        // Store in sessionStorage for page reload recovery
        sessionStorage.setItem("temp_booking_ref", bookingRefNo);
      }

      if (!currentPortalBookingNo) {
        const formattedTempPayload = formatTempBookingData({
          repriceResponse: flightReviewDetails,
          travellerInfos: travellerInfo,
          totalSeatsAmount,
          totalBaggageAmount,
          totalWheelChairAmount,
          totalMealAmount,
          convenienceFeeWithGST,
          appliedPromo,
          appliedPromoAmount,
          appliedGiftCode,
          appliedGiftCodeAmount,
          walletAmount,
          walletRefundableAmount,
          walletNonRefundableAmount,
          creditLimitAmount,
          convenienceFeeGSTPER: handlingFees?.GSTPer,
          tripType: location.state.tripType,
          passengerCount: location.state,
          deliveryInfo: {
            emails: [values?.email_id].filter(Boolean),
            contacts: [values?.mobile_no]
              .filter(Boolean)
              ?.map((e: any) => `${e}`),
          },
          ...(values?.isGST && {
            gstInfo: {
              gstNumber: values?.gst_number,
              registeredName: values?.gst_registered_name,
              address: values?.gst_address,
              state_id: values?.state_id,
            },
          }),
        });

        const tempMainPayload = {
          ...formattedTempPayload,
          booking_no: bookingRefNo,
          from_location: location.state.fromLocation,
          to_location: location.state.toLocation,
        };

        tempRes = await dispatch(saveTempFlightBooking(tempMainPayload));

        if (!tempRes?.payload?.portal_booking_no) {
          throw new Error("Save temp booking failed");
        }

        currentPortalBookingNo = tempRes?.payload?.portal_booking_no;
        setPortalBookingNo(currentPortalBookingNo);
        sessionStorage.setItem("portal_booking_no", currentPortalBookingNo);
      }

      if (totalAmount > 1) {
        const orderResponse: any = await dispatch(
          createPaymentOrder({
            Amount: Math.floor(totalAmount * 100),
            Currency: "INR",
          })
        );

        const orderDetails = orderResponse?.payload?.Data;
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: orderDetails?.Amount,
          currency: orderDetails?.Currency,
          name: "BIGSMILE HOLIDAYS PRIVATE LIMITED",
          description: `transction_${flightReviewDetails?.bookingId}`,
          order_id: orderDetails?.Orderid,
          modal: {
            ondismiss: function () {
              setIsProcessingPayment(false);
            },
          },
          handler: async function (response: any) {
            clearTimeout(paymentTimeout);
            const orderDetails: any = await dispatch(
              verifyPayment({
                RazorpayOrderId: response.razorpay_order_id,
                RazorpayPaymentId: response.razorpay_payment_id,
                RazorpaySignature: response.razorpay_signature,
                booking_no: currentPortalBookingNo,
              })
            );

            if (orderDetails?.meta?.requestStatus === "fulfilled") {
              await handleSuccessfulPayment(currentPortalBookingNo, values);
            } else {
              console.error("Payment verification failed:", orderDetails);
            }
          },
          theme: {
            color: "green",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();

        const paymentTimeout = setTimeout(() => {
          setIsProcessingPayment(false);
        }, 12 * 60 * 1000);

        rzp.on("payment.failed", function (response: any) {
          clearTimeout(paymentTimeout);
          ErrorToast(`Payment failed: ${response.error.description}`);
          setIsProcessingPayment(false);
          console.error("Payment failed", response);
        });
      } else {
        await handleZeroAmountPayment(currentPortalBookingNo, values);
      }
    } catch (error) {
      setIsProcessingPayment(false);
      ErrorToast("Booking failed. Please try again.");
      console.error("Booking error:", error);
      // navigate(-1);
    }
  };

  const handleSuccessfulPayment = async (bookingNo: string, values: any) => {
    try {
      const paymentRes = await dispatch(
        addPayment({
          ClientRefNo: bookingNo,
          RefNo: bookingNo,
          TransactionType: 0,
          ProductId: "1",
        })
      );

      if (paymentRes?.payload?.Amount) {
        await confirmAndProcessBooking(bookingNo, values);
        sessionStorage.removeItem("temp_booking_ref");
        sessionStorage.removeItem("portal_booking_no");
        setTempBookingRef(null);
        setPortalBookingNo(null);
      } else {
        ErrorToast("Payment recording failed. Please try again.");
        setIsProcessingPayment(false); // Allow retry with same booking
      }
    } catch (error) {
      ErrorToast("Payment recording failed. Please try again.");
      setIsProcessingPayment(false); // Allow retry with same booking
    }
  };

  const handleZeroAmountPayment = async (bookingNo: string, values: any) => {
    const paymentRes = await dispatch(
      addPayment({
        ClientRefNo: bookingNo,
        RefNo: bookingNo,
        TransactionType: 0,
        ProductId: "1",
      })
    );

    if (paymentRes?.payload?.Amount) {
      await confirmAndProcessBooking(bookingNo, values);
    }
  };

  const confirmAndProcessBooking = async (bookingNo: string, values: any) => {
    const bookingPayload = {
      Booking_RefNo: bookingNo,
      Ticketing_Type: "1",
    };

    const bookingRes = await dispatch(confirmFlightBooking(bookingPayload));

    if (!bookingRes.payload?.portal_booking_no) {
      ErrorToast(
        "We encountered an issue with the booking. Our team will update you shortly, or a refund will be processed within 3–5 business days."
      );
      navigate("/flights");
      return;
    }

    const airResponse = await dispatch(
      getBookingDetails({
        Booking_RefNo: bookingRes?.payload?.portal_booking_no,
        Airline_PNR: "",
      })
    );

    if (airResponse?.payload?.Response_Header?.Error_Code === "0000") {
      const formatedPaylod = await formatBookingData({
        ...airResponse.payload,
        convenienceFeeWithGST,
        convenienceFeeGSTPER: handlingFees?.GSTPer,
        appliedPromo,
        appliedPromoAmount,
        appliedGiftCode,
        appliedGiftCodeAmount,
        walletAmount,
        creditLimitAmount,
        walletRefundableAmount,
        walletNonRefundableAmount,
        gst_state_id: values?.state_id,
        tripType:
          findBookingType[airResponse.payload?.Booking_Type] || "ONEWAY",
      });

      const mainPayload = {
        ...formatedPaylod,
        from_location: location.state.fromLocation,
        to_location: location.state.toLocation,
      };

      const lastRes = await dispatch(saveFlightBooking(mainPayload));

      if (lastRes.meta.requestStatus === "fulfilled") {
        navigateToConfirmationPage(
          bookingRes.payload?.Response_Header?.Status_Id,
          bookingRes?.payload?.portal_booking_no
        );
      }
    }
  };

  const navigateToConfirmationPage = (statusId: string, bookingNo: string) => {
    sessionStorage.removeItem("temp_booking_ref");
    sessionStorage.removeItem("portal_booking_no");
    setTempBookingRef(null);
    setPortalBookingNo(null);
    setIsProcessingPayment(false);
    switch (statusId) {
      case "11":
        navigate(`/flights/trip-confirmation?bookingId=${bookingNo}`);
        break;
      case "22":
        navigate(`/flights/pending-confirmation?bookingId=${bookingNo}`);
        break;
      case "33":
        navigate(`/flights/blocked-confirmation?bookingId=${bookingNo}`);
        break;
      default:
        navigate(`/flights/failed-confirmation?bookingId=${bookingNo}`);
    }
  };

  return { handleSubmit };
};
